// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { LawTopic } from '../../types/admin';
import { RichTextEditor } from './RichTextEditor';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { AdminImportDialog } from './AdminImportDialog';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { useAdminToast } from './useAdminToast';

interface AdminTopicsProps {
  session: Session;
  profile: Profile | null;
  showCreateForm?: boolean;
}

export const AdminTopics: React.FC<AdminTopicsProps> = ({ session, profile, showCreateForm = false }) => {
  const [topics, setTopics] = useState<LawTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(showCreateForm);
  const [editingTopic, setEditingTopic] = useState<LawTopic | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    rss_feed_url: '',
    is_active: true,
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const { showSuccess, showError } = useAdminToast();

  useEffect(() => {
    loadTopics();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-topics-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'law_topics',
        },
        () => {
          loadTopics();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadTopics = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('law_topics')
        .select('*')
        .order('name');

      if (error) throw error;
      setTopics(data || []);
    } catch (err) {
      console.error('Error loading topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTopic) {
        const { error } = await supabase
          .from('law_topics')
          .update(formData)
          .eq('id', editingTopic.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('law_topics').insert(formData);
        if (error) throw error;
      }
      setShowForm(false);
      setEditingTopic(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        rss_feed_url: '',
        is_active: true,
      });
      await loadTopics();
      showSuccess(editingTopic ? 'Topic updated successfully' : 'Topic created successfully');
    } catch (err: any) {
      console.error('Error saving topic:', err);
      showError(err.message || 'Failed to save topic');
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteTarget(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const { error } = await supabase.from('law_topics').delete().eq('id', deleteTarget);
      if (error) throw error;
      await loadTopics();
      showSuccess('Topic deleted successfully');
    } catch (err: any) {
      console.error('Error deleting topic:', err);
      showError(err.message || 'Failed to delete topic');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleExportCSV = () => {
    const csvData = topics.map((topic) => ({
      Name: topic.name,
      Slug: topic.slug,
      Description: topic.description || '',
      'RSS Feed URL': topic.rss_feed_url,
      Active: topic.is_active ? 'Yes' : 'No',
      'Created At': format(new Date(topic.created_at), 'yyyy-MM-dd HH:mm:ss'),
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `topics_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async (data: any[]) => {
    try {
      // Transform imported data to match topic structure
      const topicsToImport = data.map((row) => {
        // Generate slug from name if not provided
        const slug = row.slug || row['Slug'] || row.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';
        
        return {
          name: row.name || row['Name'] || '',
          slug: slug,
          description: row.description || row['Description'] || '',
          rss_feed_url: row.rss_feed_url || row['RSS Feed URL'] || '',
          is_active: row.is_active !== undefined 
            ? row.is_active 
            : row['Active'] !== undefined 
              ? row['Active'].toString().toLowerCase() === 'yes' || row['Active'].toString().toLowerCase() === 'true'
              : true,
        };
      }).filter((topic) => topic.name); // Filter out topics without names

      if (topicsToImport.length === 0) {
        throw new Error('No valid topics to import');
      }

      // Import topics (upsert to handle duplicates by slug)
      const { error } = await supabase.from('law_topics').upsert(topicsToImport, {
        onConflict: 'slug',
        ignoreDuplicates: false,
      });

      if (error) throw error;
      await loadTopics();
      showSuccess(`Successfully imported ${topicsToImport.length} topic(s)`);
    } catch (err: any) {
      console.error('Error importing topics:', err);
      throw new Error(err.message || 'Failed to import topics');
    }
  };

  // Sample import data format - Replace with your actual RSS feed URLs when importing
  const sampleImportData = [
    {
      'Name': 'Corporate Law',
      'Slug': 'corporate-law', // URL-friendly identifier
      'Description': 'Legal matters related to corporations and businesses',
      'RSS Feed URL': 'https://eur-lex.europa.eu/legal-content/EN/RSS/?uri=CELEX:32016R0679', // Replace with actual RSS feed URL
      'Active': 'Yes', // 'Yes' or 'No'
    },
    {
      'Name': 'Employment Law',
      'Slug': 'employment-law', // URL-friendly identifier
      'Description': 'Laws governing employer-employee relationships',
      'RSS Feed URL': 'https://eur-lex.europa.eu/legal-content/EN/RSS/?uri=CELEX:32019R1152', // Replace with actual RSS feed URL
      'Active': 'Yes', // 'Yes' or 'No'
    },
  ];

  const handleEdit = (topic: LawTopic) => {
    setEditingTopic(topic);
    setFormData({
      name: topic.name,
      slug: topic.slug,
      description: topic.description || '',
      rss_feed_url: topic.rss_feed_url,
      is_active: topic.is_active,
    });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Law Topics Management
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            Manage legal topics and RSS feeds
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
          <button
            onClick={() => setShowImportDialog(true)}
            className="w-full sm:w-auto px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
            title="Import topics from CSV/JSON"
          >
            <span className="material-symbols-outlined text-sm">upload</span>
            <span>Import</span>
          </button>
          <button
            onClick={handleExportCSV}
            className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 min-h-[44px] touch-manipulation"
          >
            <span className="sm:hidden">CSV</span>
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingTopic(null);
              setFormData({
                name: '',
                slug: '',
                description: '',
                rss_feed_url: '',
                is_active: true,
              });
            }}
            className="w-full sm:w-auto px-4 py-2.5 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
          >
            Create Topic
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
            {editingTopic ? 'Edit Topic' : 'Create New Topic'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="topic-name" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Name
              </label>
              <input
                id="topic-name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[40px] touch-manipulation text-base sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="topic-slug" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Slug
              </label>
              <input
                id="topic-slug"
                type="text"
                required
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[40px] touch-manipulation text-base sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="topic-description" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Description
              </label>
              <RichTextEditor
                id="topic-description"
                content={formData.description}
                onChange={(content) => setFormData({ ...formData, description: content })}
                placeholder="Enter topic description..."
              />
            </div>
            <div>
              <label htmlFor="rss-feed-url" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                RSS Feed URL
              </label>
              <input
                id="rss-feed-url"
                type="url"
                required
                value={formData.rss_feed_url}
                onChange={(e) => setFormData({ ...formData, rss_feed_url: e.target.value })}
                className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[40px] touch-manipulation text-base sm:text-sm"
              />
            </div>
            <div className="flex items-center gap-2 min-h-[44px] sm:min-h-auto">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-luna-secondary/30 w-5 h-5 sm:w-4 sm:h-4 touch-manipulation"
              />
              <label htmlFor="is_active" className="text-sm text-luna-text-light dark:text-luna-text-dark cursor-pointer touch-manipulation">
                Active
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 min-h-[44px] sm:min-h-[40px] touch-manipulation text-base sm:text-sm font-medium"
              >
                {editingTopic ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTopic(null);
                }}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 min-h-[44px] sm:min-h-[40px] touch-manipulation text-base sm:text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-luna-secondary/10">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {topics.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
                        topic
                      </span>
                      <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                        No law topics found
                      </p>
                      <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">
                        Create your first law topic to start managing RSS feeds and subscriptions
                      </p>
                      {!showForm && (
                        <button
                          onClick={() => setShowForm(true)}
                          className="px-4 py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 text-sm"
                        >
                          Create First Topic
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                topics.map((topic) => (
                  <tr
                    key={topic.id}
                    className="border-t border-luna-secondary/10 hover:bg-luna-secondary/5"
                  >
                    <td className="px-4 py-3 text-sm text-luna-text-light dark:text-luna-text-dark">
                      {topic.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                      {topic.slug}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          topic.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                        }`}
                      >
                        {topic.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(topic)}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(topic.id)}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Topic"
        message="Are you sure you want to delete this topic? This action cannot be undone and will affect all subscriptions to this topic."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setDeleteTarget(null);
        }}
        variant="danger"
      />

      {/* Import Dialog */}
      <AdminImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
        acceptedFormats={['.csv', '.json']}
        sampleData={sampleImportData}
        title="Import Topics"
      />
    </div>
  );
};

